// ============================================================================
// My Work — the desktop view switch (Ben, 7 Oct 2026, option D).
//
// Two views: List (the one list, the account managers' choice, the default for
// everyone) and Tasks (every task across every building — the only place a
// task nobody is assigned to shows). The switch shows on a desktop. A phone
// gets the list; an explicit link to the Tasks view (a dashboard tile, an old
// /tasks bookmark) still opens it there, with the switch, so the way back is
// on screen.
//
// The last choice is remembered on that computer only (browser storage). A
// link that names a view always wins over the remembered one.
// ============================================================================
import { useEffect, useState } from 'react';

export type MyWorkView = 'list' | 'tasks';

const KEY = 'tcs.myWork.view';
const DESKTOP = '(min-width: 768px)';

export function readRemembered(): MyWorkView {
  try {
    return window.localStorage.getItem(KEY) === 'tasks' ? 'tasks' : 'list';
  } catch {
    return 'list';
  }
}

export function remember(v: MyWorkView): void {
  try {
    window.localStorage.setItem(KEY, v);
  } catch {
    /* private window or blocked storage: the choice just isn't remembered */
  }
}

export function useIsDesktop(): boolean {
  const get = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(DESKTOP).matches
    : false);
  const [on, setOn] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(DESKTOP);
    const f = () => setOn(mq.matches);
    mq.addEventListener?.('change', f);
    return () => mq.removeEventListener?.('change', f);
  }, []);
  return on;
}

const VIEWS: { v: MyWorkView; label: string }[] = [
  { v: 'list', label: 'List' },
  { v: 'tasks', label: 'Tasks' },
];

export function ViewSwitch({ view, onChange }: { view: MyWorkView; onChange: (v: MyWorkView) => void }) {
  return (
    <div role="tablist" aria-label="My Work view" className="inline-flex overflow-hidden rounded-lg border border-tcs-border bg-tcs-bg">
      {VIEWS.map(({ v, label }) => {
        const on = v === view;
        return (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(v)}
            className={
              'min-h-[32px] px-3 text-[12.5px] font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-tcs-accent ' +
              (on ? 'bg-tcs-inverse text-tcs-inverse-ink' : 'text-tcs-text-secondary hover:bg-tcs-surface')
            }
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
